import {validateSourceSceneGenerationDocumentStructure,type NativeSceneSpec,type NativeSceneGenerationContext} from '../../protocol/source-scene-generation';
import {validateSourceScriptDocument,type SourceScript} from '../../protocol/source-script-data';
import type {SourceSpeechRestore} from '../../protocol/source-script';
import {validateSourceBrandIntroDocument} from '../../protocol/source-brand-intro';
import {validateSourceTextDocument,type SourceScene} from '../../protocol/source-text';
import {sourcePageTransitions,sourcePageSampleFrames,sourcePageCardBottom,type SourcePageTransition} from './source-page-transition';
import {validateCinematicMotionDocument} from '../../protocol/cinematic-motion';
import {validateCinematicMaskDocument} from '../../protocol/cinematic-masks';
import {hasNativeCallLaneBars,validateNativeCallLaneBars} from '../../protocol/call-lane-bar';
import {isPhoneRawHandoffState} from '../../protocol/call-lane-raw';
import { z } from "zod";
import { orderTrackZones } from "./track-layout";
import type {
  UndoableState,
  TrackType,
} from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { EditorStarterItem } from "../../vendor/src/features/remotion-editor/editor/items/item-type";
import {
  defaultTextItemBackground,
  defaultTextShadow,
} from "../../vendor/src/features/remotion-editor/editor/items/text/text-item-type";
import {
  sourceCardToNativeCrop,
  sourcePositionSteps,
} from "./source-media-presentation";
import { sourceDisplay } from "./source-caption-presentation";

const finite = z.number().finite();
const positive = finite.positive();
const id = z
  .string()
  .min(1)
  .refine(
    (value) => !["__proto__", "constructor", "prototype"].includes(value),
  );
const sourceTypography = z.object({
  bar:z.object({maxRows:positive.int(),paddingX:finite.nonnegative(),paddingY:finite.nonnegative(),radius:finite.nonnegative(),color:z.string(),borderColor:z.string().optional(),borderWidth:finite.nonnegative(),shadow:z.string().optional()}).optional(),
  preserveFontSize: z.literal(true),
  verticalAlign: z.enum(["top","center"]),
  whiteSpace: z.literal("pre-line"),
  textShadow: z.string().optional(),
  wordBreak: z.enum(["keep-all", "break-all"]).optional(),
  wrap: z
    .object({
      mode: z.enum(["em", "legacy", "free"]),
      maxEm: positive.optional(),
    })
    .optional(),
  flow: z
    .object({ groupId: z.string(), order: finite, initialOffset: finite, progressStartFrame: finite.optional() })
    .optional(),
});
const sourceFrame = z.object({
  initialTop: finite,
  captionDriven: z.boolean(),
  gap: finite,
  minimumTop: finite,
  focusX: finite.min(0).max(100),
  focusY: finite.min(0).max(100),
  scale: positive,
});
const base = {
  sourceSceneSpec:z.custom<NativeSceneSpec>().optional(),
  sourceScene:z.custom<SourceScene>().optional(),
  sourceScript:z.custom<SourceScript>().optional(),
  sourceSpeechRestore:z.custom<SourceSpeechRestore>().optional(),
  id,
  from: finite.int().nonnegative(),
  durationInFrames: finite.int().positive(),
  left: finite,
  top: finite,
  width: positive,
  height: positive,
  opacity: finite.min(0).max(1),
  isDraggingInTimeline: z.boolean().optional().default(false),
  isMainTrack: z.boolean().optional(),
};
const visual = {
  ...base,
  rotation: finite.default(0),
  fadeInDurationInSeconds: finite.nonnegative().default(0),
  fadeOutDurationInSeconds: finite.nonnegative().default(0),
};
const crop = {
  assetId: id,
  borderRadius: finite.nonnegative().default(0),
  cropLeft: finite.min(0).max(1).default(0),
  cropRight: finite.min(0).max(1).default(0),
  cropTop: finite.min(0).max(1).default(0),
  cropBottom: finite.min(0).max(1).default(0),
  keepAspectRatio: z.boolean().default(true),
  sourceFrame: sourceFrame.optional(),
};
const sound = {
  assetId: id,
  audioStartFromInSeconds: finite.nonnegative().default(0),
  playbackRate: positive.default(1),
  decibelAdjustment: finite.default(0),
  audioFadeInDurationInSeconds: finite.nonnegative().default(0),
  audioFadeOutDurationInSeconds: finite.nonnegative().default(0),
};
const font = {
  fontFamily: z.string().min(1),
  fontStyle: z.object({ variant: z.string(), weight: z.string() }),
  fontSize: positive,
  lineHeight: positive,
  letterSpacing: finite,
  color: z.string(),
  align: z.enum(["left", "center", "right"]),
  direction: z.enum(["ltr", "rtl"]).default("ltr"),
  strokeWidth: finite.nonnegative(),
  strokeColor: z.string(),
  backgroundColor: z.string().optional(),
  backgroundPadding: finite.nonnegative().optional(),
  shadowEnabled: z.boolean().optional(),
  shadowColor: z.string().optional(),
  shadowBlur: finite.nonnegative().optional(),
  shadowOpacity: finite.min(0).max(1).optional(),
  shadowOffsetX: finite.optional(),
  shadowOffsetY: finite.optional(),
};
const itemSchema = z.discriminatedUnion("type", [
  z.object({ ...visual, ...crop, type: z.literal("image") }).passthrough(),
  z
    .object({
      ...visual,
      ...crop,
      ...sound,
      type: z.literal("video"),
      videoStartFromInSeconds: finite.nonnegative().default(0),
      muteAudio: z.boolean().optional(),
      audioRemoved: z.boolean().optional(),
    })
    .passthrough(),
  z
    .object({
      ...base,
      ...sound,
      type: z.literal("audio"),
      width: finite.nonnegative(),
      height: finite.nonnegative(),
    })
    .passthrough(),
  z
    .object({
      ...visual,
      type: z.literal("solid"),
      color: z.string(),
      borderRadius: finite.nonnegative().default(0),
      keepAspectRatio: z.boolean().default(false),
    })
    .passthrough(),
  z
    .object({
      ...visual,
      ...font,
      type: z.literal("text"),
      text: z.string(),
      resizeOnEdit: z.boolean().default(false),
    })
    .passthrough(),
  z
    .object({
      ...visual,
      ...font,
      type: z.literal("captions"),
      assetId: id,
      highlightColor: z.string().optional(),
      pageDurationInMilliseconds: positive,
      captionStartInSeconds: finite.nonnegative(),
      maxLines: positive.int(),
      sourceTypography: sourceTypography.optional(),
    })
    .passthrough(),
]);
const assetBase = {
  id,
  filename: z.string(),
  size: finite.nonnegative(),
  remoteUrl: z.string().nullable(),
  remoteFileKey: z.string().nullable(),
  mimeType: z.string(),
};
const assetSchema = z.discriminatedUnion("type", [
  z
    .object({
      ...assetBase,
      type: z.literal("image"),
      width: positive,
      height: positive,
    })
    .passthrough(),
  z
    .object({
      ...assetBase,
      type: z.literal("video"),
      width: positive,
      height: positive,
      durationInSeconds: positive,
      hasAudioTrack: z.boolean(),
    })
    .passthrough(),
  z
    .object({
      ...assetBase,
      type: z.literal("audio"),
      durationInSeconds: positive,
    })
    .passthrough(),
  z
    .object({
      ...assetBase,
      type: z.literal("caption"),
      captionLanguage: z.string(),
      captions: z.array(
        z
          .object({
            text: z.string(),
            startMs: finite,
            endMs: finite,
            timestampMs: finite.nullable(),
            confidence: finite.nullable(),
          })
          .passthrough(),
      ),
    })
    .passthrough(),
]);
const trackSchema = z
  .object({
    id,
    items: z.array(id),
    hidden: z.boolean(),
    muted: z.boolean(),
    locked: z.boolean().default(false),
    magnetic: z.boolean().optional(),
    zone: z.enum(["visual", "audio"]).optional(),
    category: z.enum(["video", "audio", "text"]).optional(),
    metadata: z
      .object({
        isMainTrack: z.boolean(),
        type: z.enum(["video", "image", "audio", "caption", "text"]),
        trackType: z.enum(["main"]).nullable(),
        kind: z.literal("bgm").optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
const documentSchema = z.object({
  sourceSceneGeneration:z.custom<NativeSceneGenerationContext>().optional(),
  fps: positive,
  compositionWidth: positive,
  compositionHeight: positive,
  durationInFrames: positive.int(),
  backgroundColor: z.string(),
  items: z.record(itemSchema),
  assets: z.record(assetSchema),
  tracks: z.array(trackSchema),
  transitions: z.record(z.unknown()).optional(),
  deletedAssets: z.array(z.unknown()).optional(),
  mask: z.unknown().optional(),
  transferReport: z
    .object({
      warnings: z
        .array(z.object({ code: z.string() }).passthrough())
        .optional(),
    })
    .passthrough()
    .optional(),
});
type LegacyItem = z.infer<typeof itemSchema>;
type Parsed = z.infer<typeof documentSchema>;
export type MigrationResult =
  | {
      ok: true;
      document: UndoableState;
      itemIds: string[];
      assetIds: string[];
      legacyArchive: unknown;
    }
  | { ok: false; reason: string };

function textDecorations(item: z.infer<typeof itemSchema>) {
  if (item.type !== "text" && item.type !== "captions") return {};
  const pad = item.backgroundPadding ?? 0;
  return {
    background: {
      ...defaultTextItemBackground,
      visible: !!item.backgroundColor && item.backgroundColor !== "transparent",
      color: item.backgroundColor ?? "transparent",
      horizontalPadding: pad * 1.5,
      verticalPadding: pad,
      borderRadius: 4,
      verticalOffset: 0,
    },
    shadow: {
      ...defaultTextShadow,
      visible: item.shadowEnabled === true,
      opacity: item.shadowOpacity ?? defaultTextShadow.opacity,
      color: item.shadowColor ?? "#000000",
      blur: item.shadowBlur ?? 0,
      offsetX: item.shadowOffsetX ?? 0,
      offsetY: item.shadowOffsetY ?? 0,
    },
  };
}
function captionHeight(item: LegacyItem, data: Parsed) {
  if (item.type !== "captions" || !item.sourceTypography) return item.height;
  if (item.sourceTypography.verticalAlign === 'center') return item.height;
  const asset = data.assets[item.assetId];
  if (asset?.type !== "caption") throw new Error("자막 원본 연결이 없습니다.");
  const raw = asset.captions.map((caption) => caption.text).join("");
  return (
    Math.max(
      1,
      sourceDisplay(
        raw,
        item.width,
        item.fontSize,
        item.sourceTypography,
      ).split("\n").length,
    ) *
    item.fontSize *
    item.lineHeight + 2*(item.sourceTypography.bar?.paddingY??0)
  );
}
function captionTop(item: LegacyItem, data: Parsed, frame: number) {
  if (item.type !== "captions" || !item.sourceTypography?.flow) return item.top;
  const flow = item.sourceTypography.flow;
  const offset = Object.values(data.items).reduce(
    (sum, prior) =>
      prior.type === "captions" &&
      prior.sourceTypography?.flow?.groupId === flow.groupId &&
      prior.sourceTypography.flow.order < flow.order &&
      prior.from <= frame &&
      prior.from + prior.durationInFrames > frame
        ? sum + captionHeight(prior, data)
        : sum,
    0,
  );
  return item.top - flow.initialOffset + offset;
}
function presentedTop(item: LegacyItem, data: Parsed, frame: number, transitions: readonly SourcePageTransition[]) {
  if (item.type === "captions") return captionTop(item, data, frame);
  if (
    (item.type === "image" || item.type === "video") &&
    item.sourceFrame?.captionDriven
  ) {
    const source = item.sourceFrame;
    const activeGroups=new Set<string>();
    for(const caption of Object.values(data.items))if(caption.type==='captions'&&caption.sourceTypography&&caption.from<=frame&&caption.from+caption.durationInFrames>frame)activeGroups.add(caption.sourceTypography.flow?.groupId??'');
    const bottom = Object.values(data.items).reduce(
      (max, caption) =>
        caption.type === "captions" &&
        caption.sourceTypography &&
        caption.from <= frame &&
        caption.from + caption.durationInFrames > frame
          ? Math.max(
              max,
              captionTop(caption, data, frame) +
                captionHeight(caption, data),
            )
          : max,
      -Infinity,
    );
    return item.top - source.initialTop + sourcePageCardBottom(transitions,frame,bottom,activeGroups,source.minimumTop,source.gap);
  }
  return item.top;
}

/** Prepare a separate native document; failure never changes the original state. */
export function migrateLegacyDocument(
  value: unknown,
  measuredMedia: Record<string, { width: number; height: number;durationInSeconds?:number|null }> = {},
): MigrationResult {
  if(!validateSourceSceneGenerationDocumentStructure(value))return {ok:false,reason:'씬 설정과 생성 원본의 연결을 확인하지 못했습니다.'};
  if(!validateSourceScriptDocument(value))return {ok:false,reason:'대본과 보관한 복원 원본을 확인하지 못했습니다.'};
  if(!validateSourceBrandIntroDocument(value))return {ok:false,reason:'오프닝 카드 설정을 확인하지 못했습니다.'};
  if(!validateSourceTextDocument(value))return {ok:false,reason:'화면 글자와 씬 연결을 확인하지 못했습니다.'};
  const phoneTagged=typeof value==='object'&&value!==null&&'version' in value&&value.version==='1.1.0';
  if((phoneTagged||hasNativeCallLaneBars(value))
      &&(!isPhoneRawHandoffState(value)||!validateNativeCallLaneBars(value)))
    return {ok:false,reason:'통화 화면의 저장 형식과 시각 원본을 확인하지 못했습니다.'};
  const parsed = documentSchema.safeParse(value);
  if (!parsed.success)
    return {
      ok: false,
      reason:
        "현재 저장 형식에 아직 대조하지 못한 항목이 있습니다. 원본을 유지했습니다.",
    };
  const data = parsed.data;
  if(!validateCinematicMotionDocument(data))return {ok:false,reason:'모션 카드 설정을 확인하지 못했습니다.'};
  if(!validateCinematicMaskDocument(data))return {ok:false,reason:'모자이크 설정을 확인하지 못했습니다.'};
  try {
    if (
      Object.keys(data.transitions ?? {}).length ||
      (data.deletedAssets?.length ?? 0) > 0 ||
      data.mask != null
    ) {
      throw new Error(
        "기존 전환 효과·삭제 기록·화면 마스크의 대조가 필요합니다. 원본을 유지했습니다.",
      );
    }
    if (
      data.transferReport?.warnings?.some(
        (warning) => warning.code === "flattened-video-fallback",
      ) ||
      Object.keys(data.items).some(
        (key) =>
          key === "waka-item-recovered-main-video" ||
          key ===
            "waka-item-726f6c653a7363656e6573-666c617474656e65642d6d61696e2d766964656f",
      )
    ) {
      throw new Error(
        "완성 영상 기반 저장본은 원본 에셋 복구가 먼저 필요합니다.",
      );
    }
    const allIds = new Set(Object.keys(data.items));
    if (
      new Set(data.tracks.map((track) => track.id)).size !== data.tracks.length
    )
      throw new Error("트랙 식별자가 중복됐습니다.");
    for (const [key, asset] of Object.entries(data.assets))
      if (key !== asset.id) throw new Error("에셋 식별자가 일치하지 않습니다.");
    for (const [key, item] of Object.entries(data.items)) {
      if (key !== item.id || !allIds.has(item.id))
        throw new Error("항목 식별자가 일치하지 않습니다.");
      if (item.animationType && item.animationType !== "none")
        throw new Error("기존 텍스트 효과 대조가 필요합니다.");
      if (item.kenBurnsEffect && item.kenBurnsEffect !== "none")
        throw new Error("기존 이미지 모션 대조가 필요합니다.");
      if (
        Array.isArray(item.positionKeyframes) &&
        item.positionKeyframes.length
      )
        throw new Error("기존 위치 키프레임 대조가 필요합니다.");
      if (
        "assetId" in item &&
        typeof item.assetId === "string" &&
        !data.assets[item.assetId]
      )
        throw new Error("원본 에셋이 누락됐습니다.");
      if (item.type === "video" || item.type === "image") {
        if (data.assets[item.assetId]?.type !== item.type)
          throw new Error("영상·이미지 원본 종류가 일치하지 않습니다.");
        if (
          item.cropLeft + item.cropRight >= 1 ||
          item.cropTop + item.cropBottom >= 1
        )
          throw new Error("이미지 자르기 범위를 확인해 주세요.");
      }
      if (item.type === "audio" && data.assets[item.assetId]?.type !== "audio")
        throw new Error("음성 원본 종류가 일치하지 않습니다.");
      if (
        item.type === "captions" &&
        data.assets[item.assetId]?.type !== "caption"
      )
        throw new Error("자막 원본 종류가 일치하지 않습니다.");
    }
    const placed = new Set<string>();
    for (const track of data.tracks)
      for (const key of track.items) {
        if (!allIds.has(key) || placed.has(key))
          throw new Error("트랙과 항목의 연결이 올바르지 않습니다.");
        placed.add(key);
      }
    if (placed.size !== allIds.size)
      throw new Error("트랙에 연결되지 않은 편집 항목이 있습니다.");
    if (
      Math.max(
        0,
        ...Object.values(data.items).map(
          (item) => item.from + item.durationInFrames,
        ),
      ) !== data.durationInFrames
    )
      throw new Error("작업 길이와 마지막 편집 구간의 대조가 필요합니다.");
    const hiddenItems=new Set(data.tracks.filter(track=>track.hidden).flatMap(track=>track.items));
    const unavailablePages=new Set(Object.values(data.items).flatMap(item=>
      item.type==='captions'&&item.sourceTypography?.flow&&(hiddenItems.has(item.id)||item.opacity!==1)
        ?[item.sourceTypography.flow.groupId]:[]));
    const pageTransitions=sourcePageTransitions(Object.values(data.items).flatMap(item=>{
      if(item.type!=='captions'||!item.sourceTypography?.flow||unavailablePages.has(item.sourceTypography.flow.groupId))return [];
      const flow=item.sourceTypography.flow;
      return [{groupId:flow.groupId,order:flow.order,from:item.from,to:item.from+item.durationInFrames,baseTop:item.top-flow.initialOffset,height:captionHeight(item,data),progressStartFrame:flow.progressStartFrame}];
    }));
    const edges = [
      ...new Set([
        0,
        ...Object.values(data.items)
          .filter((item) => item.type === "captions")
          .flatMap((item) => [item.from, item.from + item.durationInFrames]),
      ]),
    ].sort((a, b) => a - b);
    const cardEdges=[...new Set([...edges,...sourcePageSampleFrames(pageTransitions)])].sort((a,b)=>a-b);
    const items: Record<string, EditorStarterItem> = {};
    for (const item of Object.values(data.items)) {
      let next: EditorStarterItem;
      if (item.type === "audio" || item.type === "solid") next = { ...item };
      else if (item.type === "text")
        next = { ...item, ...textDecorations(item) };
      else if (item.type === "captions") {
        if (item.sourceTypography?.textShadow&&!/^0 [\d.]+px [\d.]+px rgba\(0,0,0,0\.(?:92|55)\)$/.test(item.sourceTypography.textShadow))
          throw new Error("원본 자막 그림자 대조가 필요합니다.");
        const source = item.sourceTypography;
        next = {
          ...item,
          ...textDecorations(item),
          height: captionHeight(item, data),
          ...(source
            ? {
                sourceTypography: {
                  ...source,
                  referenceWidth: item.width,
                  referenceFontSize: item.fontSize,
                },
              }
            : {}),
        };
      } else {
        next = {
          ...item,
          ...(item.type === "video"
            ? {
                muteAudio:
                  item.muteAudio === true || item.audioRemoved === true,
              }
            : {}),
        };
        if (item.sourceFrame) {
          const metadata = measuredMedia[item.assetId];
          if (!metadata)
            throw new Error("원본 영상·이미지의 실제 크기 확인이 필요합니다.");
          next = sourceCardToNativeCrop(next, metadata, item.sourceFrame);
        }
      }
      if (
        item.type === "captions" ||
        ((item.type === "image" || item.type === "video") &&
          item.sourceFrame?.captionDriven)
      ) {
        const samples = [
          item.from,
          ...(item.type==='captions'?edges:cardEdges).filter(
            (frame) =>
              frame > item.from && frame < item.from + item.durationInFrames,
          ),
        ].map((frame) => ({
          frame: frame - item.from,
          left: next.left,
          top: next.top + presentedTop(item, data, frame, pageTransitions) - item.top,
        }));
        if (
          "positionKeyframes" in next ||
          next.type === "captions" ||
          next.type === "image" ||
          next.type === "video"
        ) {
          next = {
            ...next,
            positionKeyframes: sourcePositionSteps(
              next,
              item.durationInFrames,
              samples,
            ),
          };
        }
      }
      if(next.sourceScene?.anchor)next={...next,sourceScene:{...next.sourceScene,nativeReference:{top:next.top,height:next.height}}};
      items[item.id] = next;
    }
    const tracks: TrackType[] = data.tracks.map((track) => ({
      ...track,
      magnetic: track.magnetic ?? track.metadata?.type === "video",
      zone:
        track.zone ??
        (track.metadata?.type === "audio" ||
        track.category === "audio" ||
        (track.items.length > 0 &&
          track.items.every((key) => items[key]?.type === "audio"))
          ? "audio"
          : "visual"),
    }));
    const assets = { ...data.assets };
    for (const [key, meta] of Object.entries(measuredMedia)) {
      if (
        ![meta.width, meta.height].every(
          (value) => Number.isFinite(value) && value > 0,
        )
      )
        throw new Error("실측한 원본 크기가 올바르지 않습니다.");
      const asset = assets[key];
      if(asset?.type==='video'){
        const known=typeof meta.durationInSeconds==='number'&&Number.isFinite(meta.durationInSeconds)&&meta.durationInSeconds>0;
        assets[key]={...asset,width:meta.width,height:meta.height,...(known?{durationInSeconds:meta.durationInSeconds!,wakaSourceDurationUnknown:undefined}:{wakaSourceDurationUnknown:true})};
      }else if(asset?.type==='image')assets[key]={...asset,width:meta.width,height:meta.height};
    }
    return {
      ok: true,
      document: {
        ...(data.sourceSceneGeneration?{sourceSceneGeneration:data.sourceSceneGeneration}:{}),
        tracks: orderTrackZones(tracks),
        items,
        assets,
        transitions: {},
        deletedAssets: [],
        fps: data.fps,
        compositionWidth: data.compositionWidth,
        compositionHeight: data.compositionHeight,
        backgroundColor: data.backgroundColor,
      },
      itemIds: Object.keys(items),
      assetIds: Object.keys(assets),
      legacyArchive: structuredClone(value),
    };
  } catch (error) {
    return {
      ok: false,
      reason:
        error instanceof Error ? error.message : "원본 대조에 실패했습니다.",
    };
  }
}
